import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import '../styles/authCard.css';

export default function SignupPendingPage() {
  const { i18n } = useTranslation();
  const isKo = i18n.language === 'ko';
  const location = useLocation();
  const { user, resendVerifyCode } = useAuth();

  // Email priority: navigation state > current user > stored
  const email = location.state?.email
    || user?.email
    || (() => { try { return JSON.parse(localStorage.getItem('veripass_user') || '{}').email || ''; } catch { return ''; } })();

  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // null | 'sent' | 'error'
  const [resendMsg, setResendMsg] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending || !email) return;
    setResending(true);
    setResendStatus(null);
    setResendMsg('');
    try {
      await resendVerifyCode(email);
      setResendStatus('sent');
      setCooldown(60);
    } catch (err) {
      setResendStatus('error');
      if (err?.status === 429 && err?.retryAfter) {
        setCooldown(err.retryAfter);
        setResendMsg(isKo
          ? `잠시 후 다시 시도해주세요. (${err.retryAfter}초 후)`
          : `Please try again later (${err.retryAfter}s).`);
      } else {
        setResendMsg(isKo
          ? '이메일 재발송에 실패했습니다. 잠시 후 다시 시도해주세요.'
          : 'Failed to resend email. Please try again shortly.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="vp-auth-page">
      <Link to="/" className="vp-auth-logo">VeriPass</Link>

      <div className="vp-auth-card">
        <div style={{ textAlign: 'center', fontSize: 40, marginBottom: 12 }} aria-hidden="true">✉️</div>
        <h1 className="vp-auth-h1">
          {isKo ? '가입이 접수되었습니다.' : 'Sign-up received'}
        </h1>

        <p className="vp-auth-sub" style={{ whiteSpace: 'pre-line' }}>
          {email
            ? (isKo
                ? `${email}로 안내 이메일을 발송했습니다.\n이메일을 확인하고 인증을 완료해주세요.`
                : `We sent a verification email to ${email}.\nCheck your inbox to complete verification.`)
            : (isKo
                ? '안내 이메일을 발송했습니다.\n이메일을 확인하고 인증을 완료해주세요.'
                : 'We sent you a verification email.\nCheck your inbox to complete verification.')}
        </p>

        <hr className="vp-divider" />

        <div className="vp-status info">
          {isKo
            ? <>ℹ 베타 기간 중에는 결제 확인 후 1영업일 이내 계정이 활성화됩니다.<br />
                <span style={{ fontSize: 12 }}>문의: hello@whereat.kr</span></>
            : <>ℹ During beta, accounts are activated within 1 business day after payment confirmation.<br />
                <span style={{ fontSize: 12 }}>Contact: hello@whereat.kr</span></>}
        </div>

        {resendStatus === 'sent' && (
          <div className="vp-status success">
            ✓ {isKo ? '이메일을 재발송했습니다.' : 'Verification email resent.'}
          </div>
        )}
        {resendStatus === 'error' && resendMsg && (
          <div className="vp-status error">{resendMsg}</div>
        )}

        <hr className="vp-divider" />

        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            className="vp-btn-secondary"
            onClick={handleResend}
            disabled={cooldown > 0 || resending || !email}
            style={{
              opacity: (cooldown > 0 || resending || !email) ? 0.5 : 1,
              fontSize: 14,
              padding: '8px 16px',
            }}
          >
            {resending
              ? (isKo ? '발송 중...' : 'Sending...')
              : cooldown > 0
                ? (isKo ? `${cooldown}초 후 재발송 가능` : `Resend in ${cooldown}s`)
                : (isKo ? '이메일 재발송' : 'Resend email')}
          </button>
        </div>

        <div className="vp-card-footer" style={{ marginTop: 16 }}>
          <Link to="/login" className="vp-btn-secondary">
            {isKo ? '로그인 페이지로' : 'Go to sign in'}
          </Link>
        </div>
      </div>
    </div>
  );
}
